import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { FormattedNumber, injectIntl, useIntl } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';
import DatePicker from '@/shared/components/input/DatePicker';
import FormattedListValue, { listItemMsg } from '@/shared/components/FormattedListValue';
import Checkbox from '@/shared/components/input/Checkbox';
import classNames from 'classnames';
import RelationName from '@/shared/components/RelationName';
import DataCacheService from '@/shared/services/DataCacheService';
import UserSearch from '@/shared/components/input/UserSearch';
import { isBeforeMinimum } from '../../../shared/utils/validationHelpers';
import FormattedDateShort from '../../../shared/components/FormattedDateShort';
import EnumSelect from '@/shared/components/input/EnumSelect';
import { parsePastedNumericValue } from '@/shared/utils/numberInputHelpers';
import messages from '../taskMessages';
const falseFunc = () => false;

class TaskDetailsParameterValue extends PureComponent {
  state = {
    dateFocused: null,
  };

  renderSoftware = (software, softwareType) => {
    const { intl } = this.props;
    if (!software) return '';
    const softwareArray = software
      .replace(/\s/g, '')
      .split(',')
      .filter(x => x !== '');

    return (softwareArray || [])
      .map(x => intl.formatMessage(listItemMsg(softwareType, x)))
      .join(', ');
  };

  renderRelationField = parameter => {
    const { intl } = this.props;
    // Special types (list values, enums,...)
    switch (parameter.relationFieldType) {
      case 8: // AccountingSoftware
        return (
          parameter.valueNumeric !== null && (
            <FormattedListValue list="accountingsoftware" id={parameter.valueNumeric} />
          )
        );
      case 33: // receive invoice software
        return (
          parameter.valueNumeric !== null && (
            <FormattedListValue list="receiveInvoiceSoftware" id={parameter.valueNumeric} />
          )
        );
      case 9:
      case 10:
      case 11:
      case 12:
      case 26:
      case 27: {
        const user = DataCacheService.getUserName(parameter.valueText);
        return <span>{user}</span>;
      }
      case 14:
        return (
          parameter.valueNumeric !== null && (
            <FormattedListValue list="vatPeriodicity" id={parameter.valueNumeric} />
          )
        );
      case 15:
        return parameter.valueObject !== null && <RelationName relation={parameter.valueObject} />;
      case 18:
        return (
          parameter.valueNumeric !== null && (
            <FormattedListValue list="financialStatementScheme" id={parameter.valueNumeric} />
          )
        );
      case 22:
        return this.renderSoftware(parameter.valueText, 'otherSoftware');
      case 32:
        return this.renderSoftware(parameter.valueText, 'sendInvoiceSoftware');
      case 23:
        return (
          parameter.valueNumeric !== null && (
            <FormattedListValue list="socialSecurityFund" id={parameter.valueNumeric} />
          )
        );
      case 24:
        return (
          parameter.valueNumeric !== null && (
            <FormattedListValue list="socialSecretariat" id={parameter.valueNumeric} />
          )
        );
      case 28: // ShareRegister
        return (
          parameter.valueNumeric !== null && (
            <FormattedListValue list="shareRegister" id={parameter.valueNumeric} />
          )
        );
      case 35: // SendInvoiceStrategy
        return parameter.valueNumeric === null ? (
          intl.formatMessage(messages.GeneralSetting)
        ) : (
          <FormattedListValue list="sendInvoiceStrategy" id={parameter.valueNumeric} />
        );
      case 36: // peppol status
        return (
          parameter.valueNumeric !== null && (
            <FormattedListValue list="relationPeppolStatus" id={parameter.valueNumeric} />
          )
        );
      case 37: // SendInvoicePeppolReady
        return (
          parameter.valueNumeric !== null && (
            <FormattedListValue list="PeppolReadyStatus" id={parameter.valueNumeric} />
          )
        );
      case 45: // CapitalGainsTax
        return (
          parameter.valueNumeric !== null && (
            <FormattedListValue list="capitalGainsTax" id={parameter.valueNumeric} />
          )
        );
      case 47: // Statute
        return (
          parameter.valueNumeric !== null && (
            <FormattedListValue list="statute" id={parameter.valueNumeric} />
          )
        );
      default:
        break;
    }
    // Normal values
    switch (parameter.parameterValueType) {
      case 0: // text
      case 6: // textarea
        return parameter.valueText;
      case 1: // numeric
        return !parameter.valueNumeric && parameter.valueNumeric !== 0 ? null : (
          <FormattedNumber value={parameter.valueNumeric} />
        );
      case 2: // Date
        return parameter.valueDate && <FormattedDateShort value={parameter.valueDate} />;
      case 3: // boolean
        return (
          <div className="checkbox">
            <Checkbox value={parameter.valueBoolean} renderNullAsFalse readOnly disabled />
          </div>
        );
      default:
        return <span />;
    }
  };

  renderInputParameter = parameter => {
    const { onChange, intl } = this.props;
    const { dateFocused, isDateInvalid } = this.state;

    const selectionItemsArray = parameter.selectionItems
      ? parameter.selectionItems.split(';').map((x, index) => ({
          id: index,
          label: x,
        }))
      : [];

    const selectedItem =
      parameter.parameterValueType === 4 &&
      selectionItemsArray.map(x => x.label).includes(parameter.valueText)
        ? selectionItemsArray.find(x => x.label === parameter.valueText).id
        : undefined;

    const selectedItemChanged = id => {
      // selection cleared
      if (!id) {
        onChange(null, parameter, 4);
        return;
      }

      const item = selectionItemsArray.find(x => x.id === parseInt(id));
      if (!item) return;
      onChange(item.label, parameter, 4);
    };

    if (parameter.systemParameterType === 26) {
      return (
        <EnumSelect
          value={parameter.valueNumeric}
          enumType="PitTaxReturnType"
          onChange={v => onChange(v, parameter, 1)}
        />
      );
    }

    const isReadOnly = parameter.readOnly;
    switch (parameter.parameterValueType) {
      case 0: // text
        return (
          <input
            type="text"
            value={parameter.valueText || ''}
            onChange={e => onChange(e.target.value, parameter, 0)}
            className="form-control"
            maxLength="500"
            readOnly={isReadOnly}
          />
        );
      case 1: // numeric
        return (
          <input
            type="number"
            value={
              !parameter.valueNumeric && parameter.valueNumeric !== 0 ? '' : parameter.valueNumeric
            }
            min="-99999999"
            max="99999999"
            step="any"
            onPaste={e => {
              e.preventDefault();
              const pastedValue = e.clipboardData.getData('text/plain');
              const numericValue = parsePastedNumericValue(pastedValue);
              if (numericValue !== null) {
                onChange(numericValue, parameter, 1);
              }
            }}
            onChange={e => onChange(e.target.value, parameter, 1)}
            className="form-control"
            readOnly={isReadOnly}
          />
        );
      case 2: // Date
        return !isReadOnly ? (
          <div
            className={classNames({
              'has-error': isBeforeMinimum(parameter.valueDate) || isDateInvalid,
            })}
          >
            <DatePicker
              date={parameter.valueDate}
              onDateChange={(date, isValid) => {
                onChange(date, parameter, 2, isValid);
                this.setState({ isDateInvalid: isValid === false });
              }}
              focused={dateFocused}
              onFocusChange={({ focused }) => this.setState({ dateFocused: focused })}
              block
              small
              isOutsideRange={falseFunc}
              id={parameter.id}
            />
          </div>
        ) : (
          <input
            className="form-control"
            type="text"
            readOnly
            value={parameter.valueDate ? intl.formatDate(parameter.valueDate) : ''}
          />
        );
      case 3: // boolean
        return (
          <div className="checkbox">
            <Checkbox
              value={parameter.valueBoolean}
              renderNullAsFalse
              onClick={value => onChange(value, parameter, 3)}
              disabled={isReadOnly}
            />
          </div>
        );
      case 4: // selection list
        return (
          <select
            className="form-control"
            value={selectedItem}
            disabled={isReadOnly}
            onChange={e => selectedItemChanged(e.target.value)}
          >
            <option value="" />
            {selectionItemsArray.map(item => (
              <option value={item.id}>{item.label}</option>
            ))}
          </select>
        );
      case 5: // employee
        return (
          <UserSearch
            disabled={isReadOnly}
            onChange={value => onChange(value?.id, parameter, 5)}
            value={parameter.valueText}
            hideClearButton
            withUnassigned
            small
            includeInactiveUsers
          />
        );
      case 6: // textarea
        return (
          <textarea
            value={parameter.valueText || ''}
            className="form-control input-sm vertical-resize"
            readOnly={isReadOnly}
            onChange={e => onChange(e.target.value, parameter, 6)}
            maxLength={500}
            rows={10}
            style={{ minHeight: '100px' }}
          />
        );
      default:
        return <span />;
    }
  };

  render() {
    const { parameter, labelClassName, inputClassName } = this.props;
    return (
      <div className="form-group">
        <label
          className={labelClassName}
          style={{ color: parameter.computedParameterModifiedByUser ? '#b80016' : undefined }}
        >
          {!!parameter.computedParameterType && (
            <span className="fa fa-calculator" style={{ marginRight: '5px' }} />
          )}
          {parameter.name}
        </label>
        <div className={inputClassName}>
          {parameter.relationFieldType
            ? this.renderRelationField(parameter)
            : this.renderInputParameter(parameter)}
        </div>
      </div>
    );
  }
}

TaskDetailsParameterValue.propTypes = {
  parameter: PropTypes.shape({}).isRequired,
  onChange: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
  labelClassName: PropTypes.string,
  inputClassName: PropTypes.string,
};

TaskDetailsParameterValue.defaultProps = {
  labelClassName: 'col-sm-4 col-md-3',
  inputClassName: 'col-sm-8 col-md-9',
};

export default injectIntl(TaskDetailsParameterValue);
